import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Input } from '@/lib/v2/components';

export type FormInput = {
  names: string;
  quantity: number;
};

interface FormDistributeBulkProps {
  onClose: (isOpen: boolean) => void;
  onSubmit: (data: FormInput) => void;
  isLoading: boolean;
}
const MAX_GROUP_LENGTH = 100;
const MAX_PREFIX_GROUP_NAME_LENGTH = MAX_GROUP_LENGTH - ' - MM-DDTHH:MM:SS - XX'.length;
const MIN_DESTINATION_GROUPS = 2;
const MAX_DESTINATION_GROUPS = 50;

const FormDistributeBulk = ({ onClose, onSubmit, isLoading }: FormDistributeBulkProps) => {
  const { t } = useTranslation();

  const RULES = useMemo(
    () => ({
      names: {
        required: t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.emptyField'),
        maxLength: {
          value: MAX_PREFIX_GROUP_NAME_LENGTH,
          message: t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.maxLength', {
            maxLength: MAX_PREFIX_GROUP_NAME_LENGTH,
          }),
        },
      },
      quantity: {
        required: t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.emptyField'),
        min: {
          value: MIN_DESTINATION_GROUPS,
          message: t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.betweenRange'),
        },
        max: {
          value: MAX_DESTINATION_GROUPS,
          message: t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.betweenRange'),
        },
      },
    }),
    [t]
  );

  const { control, handleSubmit } = useForm<FormInput>();

  return (
    <form
      className="flex w-full flex-col items-center justify-center divide-y"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="w-full py-4 sm:w-3/4">
        <section className="mb-3 flex flex-col gap-3">
          <Input
            control={control}
            id="name-input"
            label={t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.fieldName')}
            name="names"
            rules={RULES.names}
          />
          <Input
            control={control}
            id="quantity-input"
            label={t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.fieldQuantity')}
            name="quantity"
            rules={RULES.quantity}
            type="number"
          />
        </section>
      </div>
      <div className="flex w-full justify-end gap-2 pt-2">
        <Button outline disabled={isLoading} id="close-button" onClick={() => onClose(false)}>
          {t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.close')}
        </Button>
        <Button id="confirm-button" isLoading={isLoading} type="submit">
          {t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.confirm')}
        </Button>
      </div>
    </form>
  );
};

export default FormDistributeBulk;
